/**
 * API endpoint wrappers with full type safety
 * Each function corresponds to a backend API endpoint
 */

import apiClient from './api';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  User,
  UploadedFile,
  UploadResponse,
  FileValidationResult,
  QualityMetrics,
  Job,
  JobRequest,
  Signature,
  SignatureListResponse,
  SignatureStats,
  JobResult,
  Dataset,
  DatasetSearchRequest,
  DownloadJob,
  DownloadRequest,
  RepositoryStats,
  MetadataInfo,
  StorageQuota,
  StorageStats,
  ListResponse,
  DataOverviewRequest,
  DataOverviewResponse,
  DatasetOverview,
  DatasetMergeRequest,
  FilterDatasetRequest,
  DeconvolutionRunRequest,
  DeconvolutionRunResponse,
  DatasetSource,
  AlgorithmListResponse,
  AlgorithmInfo,
  AlgorithmRecommendationsResponse,
  DeconvolutionStats,
} from './types';

/**
 * Authentication API
 */
export const authApi = {
  /**
   * Login user
   */
  login(credentials: LoginRequest) {
    return apiClient.post<AuthResponse>('/api/auth/login', credentials);
  },

  /**
   * Register new user
   */
  register(data: RegisterRequest) {
    return apiClient.post<AuthResponse>('/api/auth/register', data);
  },

  /**
   * Get current user
   */
  getCurrentUser() {
    return apiClient.get<User>('/api/auth/me');
  },

  /**
   * Logout user
   */
  logout() {
    return apiClient.post('/api/auth/logout');
  },

  /**
   * Refresh auth token
   */
  refreshToken() {
    return apiClient.post<AuthResponse>('/api/auth/refresh');
  },
};

/**
 * File Upload API
 */
export const fileApi = {
  /**
   * Upload a file
   * @param file - File to upload
   * @param onProgress - Progress callback (0-100)
   */
  uploadFile(file: File, onProgress?: (progress: number) => void) {
    return apiClient.uploadFile<UploadResponse>(
      '/api/upload',
      file,
      onProgress
    );
  },

  /**
   * List uploaded files
   */
  listFiles(page: number = 1, per_page: number = 10) {
    return apiClient.get<ListResponse<UploadedFile>>('/api/files', {
      params: { page, per_page },
    });
  },

  /**
   * Get file details
   */
  getFile(fileId: string) {
    return apiClient.get<UploadedFile>(`/api/files/${fileId}`);
  },

  /**
   * Delete file
   */
  deleteFile(fileId: string) {
    return apiClient.delete(`/api/files/${fileId}`);
  },

  /**
   * Validate uploaded file
   */
  validateFile(fileId: string) {
    return apiClient.get<FileValidationResult>(
      `/api/files/${fileId}/validate`
    );
  },

  /**
   * Get file preview
   */
  getPreview(fileId: string) {
    return apiClient.get<Record<string, unknown>>(`/api/files/${fileId}/preview`);
  },

  /**
   * Get quality metrics for file
   */
  getQualityMetrics(fileId: string) {
    return apiClient.get<QualityMetrics>(`/api/files/${fileId}/quality-metrics`);
  },

  /**
   * Download file
   */
  downloadFile(fileId: string) {
    return apiClient.downloadFile(
      `/api/files/${fileId}/download`,
      `file-${fileId}`
    );
  },
};

/**
 * Jobs API
 */
export const jobsApi = {
  /**
   * Create a new deconvolution job
   */
  createJob(data: JobRequest) {
    return apiClient.post<Job>('/api/jobs', data);
  },

  /**
   * Get all jobs for a user
   */
  listJobs(userId: number, skip: number = 0, limit: number = 100) {
    return apiClient.get<ListResponse<Job>>('/api/jobs', {
      params: { user_id: userId, skip, limit },
    });
  },

  /**
   * Get job details
   */
  getJob(jobId: string) {
    return apiClient.get<Job>(`/api/jobs/${jobId}`);
  },

  /**
   * Cancel a job
   */
  cancelJob(jobId: string) {
    return apiClient.post(`/api/jobs/${jobId}/cancel`);
  },

  /**
   * Delete a job
   */
  deleteJob(jobId: string) {
    return apiClient.delete(`/api/jobs/${jobId}`);
  },

  /**
   * Get job results
   */
  getResults(jobId: string, page: number = 1, pageSize: number = 50) {
    return apiClient.get<JobResult>(`/api/jobs/${jobId}/results`, {
      params: { page, page_size: pageSize }
    });
  },
};

/**
 * Signatures API
 */
export const signaturesApi = {
  /**
   * List all signatures
   */
  list(
    filters?: { tissue?: string; organism?: string; algorithm?: string },
    page: number = 1,
    per_page: number = 10
  ) {
    // Trailing slash matches the route FastAPI declares. Without it the backend
    // answers 307 with an absolute Location, which sends the browser off-origin
    // and defeats the dev proxy.
    return apiClient.get<SignatureListResponse>('/api/signatures/', {
      params: { ...filters, page, per_page, is_active: true },
    });
  },

  /**
   * Get signature details
   */
  get(signatureId: string) {
    return apiClient.get<Signature>(`/api/signatures/${signatureId}`);
  },

  /**
   * Get signature recommendations
   */
  getRecommendations(fileId: string) {
    return apiClient.post<Signature[]>('/api/signatures/recommend/', {
      file_id: fileId,
    });
  },

  /**
   * Get signature statistics
   */
  getStats() {
    return apiClient.get<SignatureStats>('/api/signatures/stats/overview');
  },

  /**
   * Reload signatures from disk (admin only)
   */
  reload() {
    return apiClient.post('/api/signatures/load');
  },
};

/**
 * Results API
 */
export const resultsApi = {
  /**
   * Get results for a job
   */
  get(jobId: string) {
    return apiClient.get<JobResult>(`/api/results/${jobId}`);
  },

  /**
   * Download results
   */
  download(jobId: string, format: 'csv' | 'json' | 'pdf' = 'csv') {
    return apiClient.downloadFile(
      `/api/results/${jobId}/download?format=${format}`,
      `results-${jobId}.${format}`
    );
  },

  /**
   * Get visualization data
   */
  getVisualization(jobId: string) {
    return apiClient.get<Record<string, unknown>>(`/api/results/${jobId}/visualizations`);
  },
};

/**
 * Repositories API
 */
export const repositoriesApi = {
  /**
   * List datasets
   * Backend returns a plain array, not paginated
   */
  listDatasets(
    organism: string = 'human',
    limit: number = 100,
    offset: number = 0,
    data_sources?: string[],
    use_cache: boolean = true
  ) {
    return apiClient.get<Dataset[]>(
      '/api/repositories/recount3/datasets',
      {
        params: { 
          organism, 
          limit, 
          offset,
          data_sources,
          use_cache
        },
      }
    );
  },

  /**
   * Search datasets
   * Backend returns a plain array, not paginated
   */
  searchDatasets(params: DatasetSearchRequest) {
    return apiClient.post<Dataset[]>(
      '/api/repositories/recount3/search',
      params
    );
  },

  /**
   * Get repository statistics (DEPRECATED: use getMetadataInfo instead)
   */
  getStats() {
    return apiClient.get<RepositoryStats>('/api/repositories/recount3/stats');
  },

  /**
   * Get metadata information about static files
   */
  getMetadataInfo() {
    return apiClient.get<MetadataInfo>('/api/repositories/recount3/metadata-info');
  },

  /**
   * Start dataset download
   */
  downloadDataset(request: DownloadRequest) {
    return apiClient.post<DownloadJob>(
      '/api/repositories/recount3/download',
      request
    );
  },

  /**
   * Get download job status
   */
  getDownloadStatus(jobId: string) {
    return apiClient.get<DownloadJob>(
      `/api/repositories/downloads/${jobId}`
    );
  },
};

/**
 * Storage & Quota API
 */
export const storageApi = {
  /**
   * Get user's storage quota
   */
  getQuota() {
    return apiClient.get<StorageQuota>('/api/storage/quota');
  },

  /**
   * Get storage statistics
   */
  getStats() {
    return apiClient.get<StorageStats>('/api/storage/stats');
  },
};

/**
 * Data Overview API
 */
export const dataOverviewApi = {
  /**
   * Convert downloaded dataset to h5ad format
   */
  convertToH5ad(data: DataOverviewRequest) {
    return apiClient.post<DataOverviewResponse>('/api/data-overview/convert', data, {
      timeout: 120000, // 2 minutes for conversion processing
    });
  },

  /**
   * Get dataset overview
   */
  getOverview(projectId: string, organism: 'human' | 'mouse') {
    return apiClient.get<DatasetOverview>(`/api/data-overview/overview/${projectId}`, {
      params: { organism },
    });
  },

  /**
   * List all converted datasets
   */
  listConverted() {
    return apiClient.get<string[]>('/api/data-overview/list');
  },

  /**
   * Delete converted dataset
   */
  deleteDataset(projectId: string) {
    return apiClient.delete(`/api/data-overview/delete/${projectId}`);
  },

  /**
   * Merge multiple datasets
   */
  mergeDatasets(data: DatasetMergeRequest) {
    return apiClient.post<DataOverviewResponse>('/api/data-overview/merge', data);
  },

  /**
   * Filter existing dataset (re-convert with new filters)
   */
  filterDataset(data: FilterDatasetRequest) {
    return apiClient.post<DataOverviewResponse>('/api/data-overview/convert', data);
  },
};

/**
 * Deconvolution API
 */
export const deconvolutionApi = {
  /**
   * Get list of available algorithms
   */
  listAlgorithms() {
    return apiClient.get<AlgorithmListResponse>('/api/deconvolution/algorithms');
  },

  /**
   * Get details for a specific algorithm
   */
  getAlgorithm(algorithmName: string) {
    return apiClient.get<AlgorithmInfo>(`/api/deconvolution/algorithms/${algorithmName}`);
  },

  /**
   * Get algorithm recommendations for a dataset
   * @param datasetId - Dataset ID (int for private, str for public)
   * @param datasetSource - Source of dataset ('private' or 'public')
   * @param tissueContext - Optional tissue context
   */
  getRecommendations(datasetId: number | string, datasetSource: DatasetSource, tissueContext?: string) {
    const params = new URLSearchParams();
    params.append('dataset_id', datasetId.toString());
    params.append('dataset_source', datasetSource);
    if (tissueContext) {
      params.append('tissue_context', tissueContext);
    }
    return apiClient.get<AlgorithmRecommendationsResponse>(`/api/deconvolution/recommend?${params.toString()}`);
  },

  /**
   * Run deconvolution with specified algorithm
   */
  runDeconvolution(data: DeconvolutionRunRequest) {
    return apiClient.post<DeconvolutionRunResponse>('/api/deconvolution/run', data);
  },

  /**
   * Get job status
   */
  getJobStatus(jobId: string) {
    return apiClient.get<Job>(`/api/deconvolution/jobs/${jobId}`);
  },

  /**
   * Get deconvolution results with pagination
   */
  getResults(jobId: string, page: number = 1, pageSize: number = 50) {
    return apiClient.get<JobResult>(`/api/deconvolution/results/${jobId}`, {
      params: { page, page_size: pageSize }
    });
  },

  /**
   * Export results in specified format
   */
  exportResults(jobId: string, format: 'csv' | 'json' | 'hdf5' | 'html' | 'markdown' = 'csv') {
    return apiClient.downloadFile(
      `/api/deconvolution/results/${jobId}/export?format=${format}`,
      `deconvolution-results-${jobId}.${format}`
    );
  },

  /**
   * Get deconvolution statistics
   */
  getStats() {
    return apiClient.get<DeconvolutionStats>('/api/deconvolution/stats/overview');
  },
};

/**
 * Export all API modules
 */
export default {
  auth: authApi,
  files: fileApi,
  jobs: jobsApi,
  signatures: signaturesApi,
  results: resultsApi,
  repositories: repositoriesApi,
  storage: storageApi,
  dataOverview: dataOverviewApi,
  deconvolution: deconvolutionApi,
};
